'use client';

import React from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  ShieldCheck,
  Link2,
  QrCode,
  BadgeCheck,
  ToggleRight,
  RotateCcw,
  ScrollText,
  Plug,
  Building2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  HelpCircle,
} from 'lucide-react';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import LandingFooter from '@/app/public-landing-page/components/LandingFooter';

type CapabilityStatus = 'existing-foundation' | 'prototype' | 'planned' | 'requires-validation';

const STATUS_META: Record<
  CapabilityStatus,
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  'existing-foundation': {
    label: 'Existing foundation',
    color: '#34d399',
    bg: 'rgba(52,211,153,0.1)',
    icon: <CheckCircle2 size={13} />,
  },
  prototype: {
    label: 'Prototype',
    color: '#e9a84c',
    bg: 'rgba(233,168,76,0.1)',
    icon: <Clock size={13} />,
  },
  planned: {
    label: 'Planned for operational test',
    color: '#85b7eb',
    bg: 'rgba(133,183,235,0.1)',
    icon: <HelpCircle size={13} />,
  },
  'requires-validation': {
    label: 'Requires validation',
    color: '#94a3b8',
    bg: 'rgba(148,163,184,0.1)',
    icon: <AlertTriangle size={13} />,
  },
};

const capabilities = [
  {
    icon: <Building2 size={22} />,
    title: 'Structured Credential Issuance',
    desc: 'Institution-led workflow for creating, reviewing, and confirming structured credential records for recipients.',
  },
  {
    icon: <Link2 size={22} />,
    title: 'Public Verification Links',
    desc: 'Each confirmed credential generates a shareable public link for third-party status checks without login.',
  },
  {
    icon: <QrCode size={22} />,
    title: 'QR or One-Click Verification',
    desc: 'QR codes that link directly to the verification portal, enabling fast credential status checks.',
  },
  {
    icon: <BadgeCheck size={22} />,
    title: 'Dynamic Trust Badge Preview',
    desc: 'Embeddable badge preview showing current credential status — designed for future integration.',
  },
  {
    icon: <ToggleRight size={22} />,
    title: 'Credential Status Management',
    desc: 'Issuers can update credential status (active, under review, suspended) through the institution dashboard.',
  },
  {
    icon: <RotateCcw size={22} />,
    title: 'Revocation-Ready Workflow',
    desc: 'Architecture designed to support credential revocation; requires issuer configuration before use.',
  },
  {
    icon: <ScrollText size={22} />,
    title: 'Audit Trail Foundation',
    desc: 'Timestamped record of issuer actions — who confirmed, when, and with what reference ID.',
  },
  {
    icon: <Plug size={22} />,
    title: 'Interoperability-Ready Architecture',
    desc: 'Designed to support future alignment with open standards. Validation against specific frameworks is required.',
  },
];

type MatrixRow = {
  capability: string;
  status: CapabilityStatus;
  notes: string;
};

const matrixRows: MatrixRow[] = [
  {
    capability: 'Institution dashboard',
    status: 'existing-foundation',
    notes: 'Live. Verification queue, issued records, archive tools.',
  },
  {
    capability: 'Public proof links',
    status: 'existing-foundation',
    notes: 'Live. Shareable public URLs for confirmed credentials.',
  },
  {
    capability: 'Verification reference',
    status: 'existing-foundation',
    notes: 'Live. Unique reference IDs generated on issuer confirmation.',
  },
  {
    capability: 'Supplier credential template',
    status: 'prototype',
    notes: 'Demo data only. Template design in refinement.',
  },
  {
    capability: 'QR verification',
    status: 'existing-foundation',
    notes: 'Live on certificate views. Links to verification portal.',
  },
  {
    capability: 'Embeddable badge',
    status: 'prototype',
    notes: 'UI preview only. Embed integration not yet implemented.',
  },
  {
    capability: 'Credential status management',
    status: 'prototype',
    notes: 'Issuer can update status through dashboard. API not exposed.',
  },
  {
    capability: 'Revocation workflow',
    status: 'planned',
    notes: 'Architecture designed. Issuer workflow needs operational test.',
  },
  {
    capability: 'Audit trail',
    status: 'prototype',
    notes: 'Timestamp and reference tracking in place. Structured audit export planned.',
  },
  {
    capability: 'Interoperability with external standards',
    status: 'requires-validation',
    notes: 'Designed for alignment. Validation against W3C VC or similar not completed.',
  },
];

export default function TrustLedgerProductPage() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <LandingNav />

      {/* Hero */}
      <section
        className="pt-32 pb-20 px-6 lg:px-10"
        style={{ background: 'linear-gradient(135deg, #06101e 0%, #0d1f3c 60%, #06101e 100%)' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Status badge */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                borderColor: 'rgba(233,168,76,0.4)',
                background: 'rgba(233,168,76,0.08)',
                color: '#e9a84c',
              }}
            >
              <Clock size={12} />
              Pre-commercial module
            </span>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                borderColor: 'rgba(133,183,235,0.3)',
                background: 'rgba(133,183,235,0.06)',
                color: '#85b7eb',
              }}
            >
              In active prototype refinement
            </span>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                borderColor: 'rgba(239,68,68,0.3)',
                background: 'rgba(239,68,68,0.06)',
                color: '#f87171',
              }}
            >
              <AlertTriangle size={12} />
              Not government-approved
            </span>
          </div>

          <h1 className="text-4xl lg:text-5xl font-black text-white leading-tight mb-5">
            Ertzyx Trust Ledger
          </h1>
          <p
            className="text-lg lg:text-xl leading-relaxed mb-4"
            style={{ color: 'rgba(232,237,243,0.75)' }}
          >
            Enterprise verification infrastructure inside the Ertzyx ledger.
          </p>
          <p
            className="text-base leading-relaxed mb-8 max-w-2xl"
            style={{ color: 'rgba(232,237,243,0.6)' }}
          >
            Ertzyx Trust Ledger is a pre-commercial verification module built on the broader Ertzyx
            human and institutional ledger foundation. It is designed for structured credential
            issuance, public proof links, status verification, QR or link-based validation, audit
            trail foundations, and interoperability-ready workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/trust-ledger/demo"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
              style={{ background: '#e9a84c', color: '#06101e' }}
            >
              View Public Demo
              <ArrowRight size={15} />
            </Link>
            <Link
              href="/login?next=/institution-dashboard/trust-ledger"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm border transition-all hover:bg-white/5"
              style={{ borderColor: 'rgba(232,237,243,0.2)', color: 'rgba(232,237,243,0.8)' }}
            >
              Institution Login
            </Link>
          </div>
          <p className="text-xs mt-4" style={{ color: 'rgba(232,237,243,0.4)' }}>
            Public verification works without login. Institution-side credential issuance requires
            authenticated access.
          </p>
        </div>
      </section>

      {/* Section A — Built from the Ertzyx foundation */}
      <section className="py-16 px-6 lg:px-10 border-b border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-trust-blue/20 bg-trust-blue/5 mb-4">
            <Building2 size={14} className="text-trust-blue" />
            <span className="text-xs font-bold uppercase tracking-wider text-trust-blue">
              Section A
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-4">
            Built from the Ertzyx foundation
          </h2>
          <p className="text-base leading-relaxed text-slate-600 max-w-2xl mb-6">
            Ertzyx remains a broad human and institutional ledger for achievement preservation,
            institutional memory, storytelling, public proof, and verification-led records. Trust
            Ledger is one enterprise module within that broader platform — not a separate product,
            and not a replacement for any existing Ertzyx capability.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Ertzyx',
                desc: 'Broad human and institutional ledger. Achievement, memory, storytelling, public proof.',
              },
              {
                label: 'ledger.ertzyx.com',
                desc: 'The platform application. Individuals, institutions, communities, public archives.',
              },
              {
                label: 'Trust Ledger',
                desc: 'One enterprise verification module. Credential issuance, status verification, audit foundations.',
              },
            ].map((item) => (
              <div key={item.label} className="rounded-xl border border-slate-100 bg-slate-50 p-5">
                <p className="font-bold text-trust-blue text-sm mb-2">{item.label}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section B — Supplier verification as one use case */}
      <section
        className="py-16 px-6 lg:px-10 border-b border-slate-100"
        style={{ background: '#fafbfc' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 mb-4">
            <ShieldCheck size={14} className="text-amber-700" />
            <span className="text-xs font-bold uppercase tracking-wider text-amber-700">
              Section B
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-4">
            Supplier verification as one possible use case
          </h2>
          <p className="text-base leading-relaxed text-slate-600 max-w-2xl mb-4">
            Government-facing supplier verification is one possible test scenario for Trust Ledger.
            In that scenario, a designated issuer could create a supplier credential, the supplier
            could display a public proof link or badge preview, and a third party could verify
            current status through a link, QR code, or verification reference.
          </p>
          <p className="text-sm text-slate-500 max-w-2xl mb-6 leading-relaxed">
            This workflow is designed for institutional testing and refinement. It has not been
            validated in production government procurement environments. Any deployment in
            government-facing contexts would require explicit partner agreement, compliance review,
            and formal integration planning.
          </p>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-3">
              Illustrative test scenario flow
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {[
                {
                  step: '01',
                  label: 'Issuer creates credential',
                  note: 'Designated institution issues supplier credential through dashboard',
                },
                {
                  step: '02',
                  label: 'Supplier receives proof link',
                  note: 'Shareable public link and QR code generated on confirmation',
                },
                {
                  step: '03',
                  label: 'Third party verifies status',
                  note: 'Checker opens link or scans QR — no login required',
                },
              ].map((item) => (
                <div key={item.step} className="flex-1">
                  <span className="font-mono text-lg font-black text-amber-700">{item.step}</span>
                  <p className="font-semibold text-slate-800 text-sm mt-1 mb-1">{item.label}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.note}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700 mt-4">
              Uses "could" and "designed to" intentionally — this workflow is not yet
              production-validated.
            </p>
          </div>
        </div>
      </section>

      {/* Section C — Core capabilities */}
      <section className="py-16 px-6 lg:px-10 border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-trust-blue/20 bg-trust-blue/5 mb-4">
            <ShieldCheck size={14} className="text-trust-blue" />
            <span className="text-xs font-bold uppercase tracking-wider text-trust-blue">
              Section C
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-2">Core capabilities</h2>
          <p className="text-sm text-slate-500 mb-10">
            Designed capabilities. See the status matrix below for current readiness.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {capabilities.map((cap) => (
              <div
                key={cap.title}
                className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-trust-blue/5 text-trust-blue">
                  {cap.icon}
                </div>
                <p className="font-bold text-slate-800 text-sm mb-2">{cap.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section D — Capability Status Matrix */}
      <section
        className="py-16 px-6 lg:px-10 border-b border-slate-100"
        style={{ background: '#fafbfc' }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-trust-blue/20 bg-trust-blue/5 mb-4">
            <ScrollText size={14} className="text-trust-blue" />
            <span className="text-xs font-bold uppercase tracking-wider text-trust-blue">
              Section D
            </span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-black text-slate-900 mb-2">
            Capability status matrix
          </h2>
          <p className="text-sm text-slate-500 mb-8">
            Honest readiness assessment as of prototype stage. Not a claim of production readiness.
          </p>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <div
                key={key}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ background: meta.bg, color: meta.color }}
              >
                {meta.icon}
                {meta.label}
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <div
              className="grid grid-cols-12 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100"
              style={{ background: '#f8f9fb' }}
            >
              <div className="col-span-5">Capability</div>
              <div className="col-span-3">Status</div>
              <div className="col-span-4">Notes</div>
            </div>
            {matrixRows.map((row, i) => {
              const meta = STATUS_META[row.status];
              return (
                <div
                  key={row.capability}
                  className="grid grid-cols-12 px-4 py-3.5 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 transition-colors"
                >
                  <div className="col-span-5 text-sm font-medium text-slate-800 pr-3">
                    {row.capability}
                  </div>
                  <div className="col-span-3">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                      style={{ background: meta.bg, color: meta.color }}
                    >
                      {meta.icon}
                      {meta.label}
                    </span>
                  </div>
                  <div className="col-span-4 text-xs text-slate-500 leading-relaxed">
                    {row.notes}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section E — No-overclaiming statement */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <div
            className="rounded-2xl border-2 p-8 lg:p-10"
            style={{ borderColor: 'rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.03)' }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
              >
                <AlertTriangle size={20} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 mb-3">Scope and limitations</h2>
                <p className="text-base leading-relaxed text-slate-700">
                  Ertzyx Trust Ledger is not currently presented as a Government of Canada-approved,
                  PSPC-certified, or production-integrated system. It is a pre-commercial module
                  designed for testing, validation, and refinement in institution-led and
                  government-facing verification contexts.
                </p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    'No Government of Canada approval or endorsement',
                    'No PSPC certification or official procurement status',
                    'No production integration with GC systems',
                    'No cryptographic signing, blockchain, or biometric anchoring',
                    'Verification reflects Ertzyx record status only',
                    'Does not replace credentials issued by official authorities',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-red-400 mt-0.5 flex-shrink-0">
                        <AlertTriangle size={13} />
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-16 px-6 lg:px-10 border-t border-slate-100"
        style={{ background: '#fafbfc' }}
      >
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-black text-slate-900 mb-3">
            Ready to explore Trust Ledger?
          </h2>
          <p className="text-slate-500 mb-2 text-sm leading-relaxed">
            Register your institution on Ertzyx to access the institution dashboard, issue
            credential-style records, and explore the Trust Ledger module in a testing context.
          </p>
          <p className="text-xs text-slate-400 mb-8">
            Institution-side issuance requires authenticated access. Public verification works
            without login.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              href="/register"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90 text-white"
              style={{ background: 'linear-gradient(135deg, #1B3A6B, #254E93)' }}
            >
              Register an Institution
              <ArrowRight size={15} />
            </Link>
            <Link
              href="/trust-ledger/demo"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all"
            >
              Open Verification Example
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
